import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { AxiosError } from 'axios';
import { fetchData, createQueryKey } from '../../../app/api/utils/API.utils';
import { Endpoints } from '../../../app/api/model/API.endpoints';
import { ErrorStatus } from '../../../app/api/model/API.model';
import { useDateRange } from '../../dateRanges/DateRanges.context';
import { useAuth } from '../../auth/providers/AuthProvider';
import { MetricIdEnum } from '../Metrics.model';
import { UtilizationMetricsResponse } from './Utilization.model';
import { transformUtilizationResponseToMetricData } from './utils';


interface UseUtilizationMetricsOptions {
  metricId?: MetricIdEnum;
  locationIds?: string[];
}

const useUtilizationMetrics = ({ metricId, locationIds }: UseUtilizationMetricsOptions = {}) => {
  const { dateRange } = useDateRange();
  const { logout, userId } = useAuth();

  const params = useMemo(() => ({
    periodStart: dateRange[0]?.format('YYYY-MM-DD'),
    periodEnd: dateRange[1]?.format('YYYY-MM-DD'),
    ...(locationIds?.length && { locationIds: locationIds.join(',') }),
  }), [dateRange, locationIds]);

  const queryKey = createQueryKey(Endpoints.UTILIZATION_METRICS, params, userId);

  const { data, error, isFetching } = useQuery<
    UtilizationMetricsResponse | undefined,
    AxiosError
  >({
    queryKey,
    queryFn: () => fetchData<UtilizationMetricsResponse>(Endpoints.UTILIZATION_METRICS, params),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 3,
  });

  useEffect(() => {
    if ((error?.request?.status as unknown as ErrorStatus) === ErrorStatus.UNAUTHORIZED && logout) {
      logout();
    }
  }, [error?.request?.status, logout]);

  const metricData = useMemo(() => {
    if (!data) return undefined;
    return transformUtilizationResponseToMetricData(data, metricId);
  }, [data, metricId]);

  return { metricData, error, isFetching };
};

export default useUtilizationMetrics;
